import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../app/providers/AuthProvider';
import { PageContainer, Section } from '../components/layout';
import GroupForm from '../features/groups/components/GroupForm';
import GroupList from '../features/groups/components/GroupList';
import { useGroups } from '../features/groups/hooks/useGroups';
import { useGroupCreationRequests } from '../features/groups/hooks/useGroupCreationRequests';
import { GroupCreationRequestList } from '../features/groups/components/requests';
import { Alert, Divider, Card, CardContent } from '@mui/material';
import { useAccountStatus } from '../features/auth/hooks/useAccountStatus';

const GroupsPage = () => {
  const navigate = useNavigate();
  const { user, profile, loading: authLoading, signIn } = useAuth();
  const { groups, loading, error } = useGroups();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { isPending, isSuspended, isActive } = useAccountStatus();

  // Fetch user's group creation requests (if not admin)
  const {
    requests: groupRequests,
    loading: requestsLoading,
    error: requestsError,
  } = useGroupCreationRequests();

  const handleSuccess = (
    result: { type: 'group'; groupId: string } | { type: 'request'; requestId: string },
  ) => {
    setDialogOpen(false);

    if (result.type === 'group') {
      // Admin created group directly - navigate to it
      navigate(`/groups/${result.groupId}`);
    } else {
      // User submitted request - could navigate to requests page or show success
      // For now just close dialog (success message shown in form)
    }
  };

  return (
    <PageContainer>
      <Section variant="default">
        <Stack spacing={6}>
          {/* Page Header */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={3}
          >
            <Box>
              <Typography variant="h4" gutterBottom>
                Community Groups
              </Typography>
              <Typography color="text.secondary" variant="h6">
                Browse and manage groups for the Penticton developer community.
              </Typography>
            </Box>
            <Button
              onClick={() => {
                if (!user) {
                  void signIn();
                  return;
                }
                setDialogOpen(true);
              }}
              startIcon={<AddIcon />}
              variant="contained"
              disabled={!user || isPending || isSuspended}
              size="large"
              sx={{ minWidth: { xs: '100%', md: 'auto' } }}
            >
              {!user ? 'Sign In to Create' : isPending ? 'Pending Approval' : isSuspended ? 'Account Suspended' : 'Create Group'}
            </Button>
          </Stack>

          {/* Groups Content */}
          <Box>
            {loading ? (
              <Typography color="text.secondary" variant="h6" textAlign="center">
                Loading groups…
              </Typography>
            ) : error ? (
              <Typography color="error" variant="h6" textAlign="center">
                Failed to load groups: {error.message}
              </Typography>
            ) : (
              <GroupList
                groups={groups}
                onSelect={group => navigate(`/groups/${group.id}`)}
              />
            )}
          </Box>

          {/* Group Creation Requests Section - Only for non-admins */}
          {user && profile && !profile.isAdmin && (
            <>
              <Divider sx={{ my: 4 }} />
              <Box>
                <Typography variant="h5" gutterBottom>
                  Group Creation Requests
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Track the status of your group creation requests. Pending requests are
                  awaiting admin approval.
                </Typography>
                {requestsError ? (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    Failed to load requests: {requestsError.message}
                  </Alert>
                ) : (
                  <GroupCreationRequestList
                    requests={groupRequests}
                    variant="user-detailed"
                    loading={requestsLoading}
                  />
                )}
              </Box>
            </>
          )}
        </Stack>
      </Section>

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => setDialogOpen(false)}
        open={dialogOpen}
      >
        <DialogTitle>Create a New Group</DialogTitle>
        <DialogContent>
          {profile ? (
            <GroupForm
              onSuccess={handleSuccess}
              onCancel={() => setDialogOpen(false)}
              organizerUid={profile.uid}
              organizerName={profile.displayName}
              organizerEmail={profile.email}
            />
          ) : (
            <Typography color="text.secondary">
              {authLoading ? 'Loading your profile…' : 'Sign in to create a group.'}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default GroupsPage;
